import { useState, useEffect } from "react";
import Header from "../../SuperAdmin/Header";
import Sidebar from "../../SuperAdmin/Sidebar";
import { 
  Calendar, 
  Trash2, 
  Search,
  Building2,
  Loader2,
  RefreshCw
} from "lucide-react";
import axios from "axios";
import Cookies from "js-cookie";

const Events = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, event: null });
  const [deleting, setDeleting] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:9090/apis";

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Get auth token from cookies
  const getAuthToken = () => {
    return Cookies.get("superAdminToken");
  };

  // Fetch all events
  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getAuthToken();
      
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(
        `${API_BASE_URL}/superAdmin/events`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        setEvents(response.data.data || response.data.events || []);
      } else {
        throw new Error(response.data.message || "Failed to fetch events");
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      setError(
        err.response?.data?.message || 
        err.message ||
        "Failed to fetch events"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteModal.event) return;

    setDeleting(true);
    setError(null);
    
    try {
      const token = getAuthToken();
      
      if (!token) {
        throw new Error("No authentication token found");
      }

      const eventId = deleteModal.event._id || deleteModal.event.id;
      
      const response = await axios.delete(
        `${API_BASE_URL}/superAdmin/events/${eventId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        // Remove deleted event from state
        setEvents(events.filter(
          event => (event._id || event.id) !== eventId
        ));
        setDeleteModal({ isOpen: false, event: null });
      } else {
        throw new Error("Failed to delete event");
      }
    } catch (err) {
      console.error("Error deleting event:", err);
      setError(
        err.response?.data?.message || 
        "Failed to delete event"
      );
    } finally {
      setDeleting(false);
    }
  };

  // Load events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Filter events based on search
  const filteredEvents = events.filter(event => 
    event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.adminOrganization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.collegeName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Date not set";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 overflow-hidden">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Event Management" toggleSidebar={toggleSidebar} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">All Events</h1>
                <p className="text-sm text-gray-600 mt-1">View and manage all events</p>
              </div>
              
              <button
                onClick={fetchEvents}
                disabled={loading}
                className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              </div>
            ) : (
              <>
                {/* Events Grid */}
                {filteredEvents.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-gray-700">No events found</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {searchTerm ? "Try a different search term" : "No events available"}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredEvents.map((event) => {
                      const eventId = event._id || event.id;
                      const collegeName = event.adminOrganization || 
                                         event.collegeName || 
                                         event.adminProfile?.organizationName || 
                                         "Unknown College";
                      const imageUrl = event.image || "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800";
                      
                      return (
                        <div
                          key={eventId}
                          className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden border border-gray-100"
                        >
                          {/* Image */}
                          <div className="relative h-40 overflow-hidden">
                            <img
                              src={imageUrl}
                              alt={event.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800";
                              }}
                            />
                            
                            {/* College Badge */}
                            <div className="absolute bottom-2 left-2 px-2 py-1 bg-white/90 backdrop-blur-sm rounded text-xs font-medium text-indigo-600 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              <span className="truncate max-w-[120px]">{collegeName}</span>
                            </div>

                            {/* Delete Button */}
                            <button
                              onClick={() => setDeleteModal({ isOpen: true, event })}
                              className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                              title="Delete event"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Content */}
                          <div className="p-4">
                            <h3 className="font-semibold text-gray-800 mb-1 line-clamp-1">
                              {event.title}
                            </h3>
                            
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                              {event.description || "No description"}
                            </p>

                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>{formatDate(event.eventDate)}</span>
                              <span>{event.createdBy?.name || "Unknown"}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Delete Event
            </h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete "{deleteModal.event?.title}"?
            </p>

            {error && (
              <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setDeleteModal({ isOpen: false, event: null });
                  setError(null);
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;